/**
 * RecommendedDeviceChip — the "Recommended for your <device>"
 * affordance that turns the silent capability-probe routing into
 * a *visible* trust signal. Two legitimate modes (NOT
 * legacy + new — adversarial-review Q6):
 *
 * ## Mode A — Simple device label (single-line pill)
 *
 * `<RecommendedDeviceChip label="Mac" />`
 *
 * A compact single-line affordance for surfaces that already
 * know what device the user is on and want a quiet
 * confirmation. Used by the QR origin-confirm card and the TAP
 * confirm card, where the user just clicked through to confirm
 * their device's role and a richer multi-line chip would be
 * over-loud copy.
 *
 * ## Mode B — Full server-driven recommendation (multi-line card)
 *
 * `<RecommendedDeviceChip recommended={fromBackend} />`
 *
 * Consumes the typed `RecommendedDefault` returned by
 * `POST /api/onboarding/v1/onboarding/recommended-default`.
 * Renders the device hint as the chip header and the reason slot
 * as a secondary line beneath. This is the DD-2 contract: the
 * system shows you what it noticed about your device AND names
 * the choice it made for you.
 *
 * The two modes share a name and a visual lineage but serve
 * different surfaces — Mode A confirms; Mode B recommends. The
 * `feedback_no_transitional_bridges` memory applies to legacy
 * wrappers around old code paths, not to genuinely distinct
 * use cases of one primitive.
 *
 * # Behaviour contract (both modes)
 *
 * - Renders nothing when there's no signal to render (empty
 *   label AND no recommended struct, or recommended.method ===
 *   "none" with a hard-block reason — surfaces handle that case
 *   with a dedicated banner, not a chip).
 * - Tone (info vs warning) is derived from the method when in
 *   Mode B (platform_passkey → info, fragile_redirect/tap →
 *   warning); explicit in Mode A.
 * - Single responsibility — purely visual; does not own state,
 *   does not call APIs, does not influence ceremonies.
 */

import * as React from "react";
import { Sparkles } from "lucide-react";
import { cn } from "../lib/utils";
import {
  type RecommendedDefault,
  type RecommendedMethod,
  resolveSlot,
} from "../lib/onboarding-slots";

export interface RecommendedDeviceChipProps {
  /**
   * Mode A — friendly device label from `describeDevice(...).label`.
   * Empty hides the chip. Ignored when `recommended` is set.
   */
  label?: string;
  /**
   * Mode B — server-driven full recommendation. Renders device
   * hint + reason from the slot keys.
   */
  recommended?: RecommendedDefault;
  /**
   * Visual tone. info (capable env) vs warning (fragile/VDI/kiosk).
   * In Mode B this is derived from `recommended.method` and the
   * explicit prop is ignored.
   */
  variant?: "info" | "warning";
  className?: string;
}

export function RecommendedDeviceChip({
  label,
  recommended,
  variant: variantProp = "info",
  className,
}: RecommendedDeviceChipProps) {
  // Mode B — server-driven recommendation.
  if (recommended) {
    // MethodNone is a hard-block surface concern — the chip is
    // not the right affordance for "this device can't do this."
    // The wizard's blocking banner owns that path.
    if (recommended.method === "none") return null;
    const hint = resolveSlot(recommended.deviceHintSlot);
    const reason = resolveSlot(recommended.reasonSlot);
    if (!hint) return null;
    const variant = variantForMethod(recommended.method);
    return (
      <div
        className={cn(
          "inline-flex flex-col gap-0.5 rounded-md border px-3 py-1.5 text-sm",
          variant === "info"
            ? "border-primary/30 bg-primary/5 text-primary"
            : "border-warning/30 bg-warning/5 text-warning-ink",
          className,
        )}
        role="note"
        aria-label={`Recommended sign-in method for your ${hint}`}
      >
        <div className="inline-flex items-center gap-1.5 text-xs font-medium uppercase tracking-wide opacity-80">
          <Sparkles className="h-3 w-3" aria-hidden="true" />
          <span>Recommended for your {hint}</span>
        </div>
        {reason && <p className="text-xs leading-snug opacity-90">{reason}</p>}
      </div>
    );
  }

  // Mode A — legacy label-only chip.
  if (!label) return null;
  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        variantProp === "info"
          ? "border-primary/30 bg-primary/5 text-primary"
          : "border-warning/30 bg-warning/5 text-warning-ink",
        className,
      )}
      role="note"
      aria-label={`Recommended sign-in method for your ${label}`}
    >
      <Sparkles className="h-3 w-3" aria-hidden="true" />
      <span>Recommended for your {label}</span>
    </div>
  );
}

/**
 * variantForMethod maps a recommended method to a visual tone.
 *
 * - platform_passkey, security_key, phone_qr — info (capable,
 *   low friction)
 * - tap — info (admin-issued bootstrap; the user has a code,
 *   not a problem; adversarial-review Q11 moved this out of
 *   warning to match the actual user experience)
 * - fragile_redirect — warning (genuine off-device fallback;
 *   the user has to leave the current device to complete)
 * - none — caller is responsible for not rendering the chip
 */
function variantForMethod(method: RecommendedMethod): "info" | "warning" {
  switch (method) {
    case "platform_passkey":
    case "security_key":
    case "phone_qr":
    case "tap":
      return "info";
    case "fragile_redirect":
      return "warning";
    case "none":
    default:
      return "info";
  }
}
